import { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/DashboardLayout";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import SetPassword from "@/pages/SetPassword";
import Legal from "@/pages/Legal";
import SmsOptIn from "@/pages/SmsOptIn";
import SaveCard from "@/pages/SaveCard";
import Dashboard from "@/pages/Dashboard";
import Dispatch from "@/pages/Dispatch";
import Manifests from "@/pages/Manifests";
import ManifestForm from "@/pages/ManifestForm";
import ManifestView from "@/pages/ManifestView";
import Bookings from "@/pages/Bookings";
import Inbox from "@/pages/Inbox";
import Invoices from "@/pages/Invoices";
import InvoiceView from "@/pages/InvoiceView";
import Vehicles from "@/pages/Vehicles";
import Chauffeurs from "@/pages/Chauffeurs";
import Team from "@/pages/Team";
import Settings from "@/pages/Settings";
import Profile from "@/pages/Profile";
import Plans from "@/pages/Plans";
import DriverTrip from "@/pages/DriverTrip";
import TrackTrip from "@/pages/TrackTrip";
import DriverPortal from "@/pages/DriverPortal";
import ClientPortal from "@/pages/ClientPortal";
import PublicBooking from "@/pages/PublicBooking";
import RouteLanding from "@/pages/RouteLanding";
import RiderApp from "@/pages/RiderApp";
import RiderTrips from "@/pages/RiderTrips";
import PortalAccount from "@/pages/PortalAccount";
import Winback from "@/pages/Winback";
import Affiliates from "@/pages/Affiliates";
import Payouts from "@/pages/Payouts";
import AirportManifests from "@/pages/AirportManifests";
import Door from "@/pages/Door";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AdminGate from "@/pages/AdminGate";
import DriverManifests from "@/pages/DriverManifests";
import { AppManifest } from "@/components/AppManifest";
import { WrongDoor } from "@/components/WrongDoor";
import { LockedFeature } from "@/components/LockedFeature";
import DriverEarnings from "@/pages/DriverEarnings";
import DispatchLive from "@/pages/DispatchLive";
import TripReceipt from "@/pages/TripReceipt";
import PayTrip from "@/pages/PayTrip";
import StatementView from "@/pages/StatementView";
import CorporatePortal from "@/pages/CorporatePortal";
import Corporate from "@/pages/Corporate";
import Community from "@/pages/Community";
import CommunityRoom from "@/pages/CommunityRoom";
import CommunityBridge from "@/pages/CommunityBridge";
import { CommunityFrame } from "@/components/community/Frame";
import BookingPaid from "@/pages/BookingPaid";
import Testers from "@/pages/Testers";
import Blog from "@/pages/Blog";
import BlogDesk from "@/pages/BlogDesk";
import OperatorBlog from "@/pages/OperatorBlog";
import { PaymentCancel, PaymentSuccess } from "@/pages/Payment";
import "@/index.css";

const Spinner = () => (
  <div className="flex min-h-screen items-center justify-center bg-ink-900">
    <Loader2 className="h-5 w-5 animate-spin text-gold" />
  </div>
);

export const homeForRole = (role) =>
  role === "driver" ? "/portal/driver" : role === "client" ? "/portal/client" : "/app";

// Each app has its own PWA scope, so an unauthenticated visitor must land on a sign-in page that
// lives INSIDE that scope — otherwise the installed app kicks them out to a browser tab.
export const loginForPath = (pathname) =>
  pathname.startsWith("/portal/client") ? "/portal/client/login"
    : pathname.startsWith("/portal/driver") ? "/portal/driver/login"
    : pathname.startsWith("/admin") ? "/admin"
    : "/app/login";

function Guard({ roles, perm, flag, flagPlan, flagTitle, flagBody, layout = false, children }) {
  const { user, loading, can, hasFlag } = useAuth();
  const { pathname } = useLocation();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to={loginForPath(pathname)} replace />;
  if (roles && !roles.includes(user.role)) {
    const expected = roles.includes("driver") ? "driver" : roles.includes("client") ? "client" : "office";
    return <WrongDoor expected={expected} />;
  }
  if (perm && !can(perm)) {
    return (
      <DashboardLayout>
        <div data-testid="no-permission" className="rounded-xl border border-ink-600 bg-ink-800 p-10 text-center">
          <p className="text-lg text-slate-100">You don't have access to this area</p>
          <p className="mt-2 text-sm text-slate-400">Ask the owner to grant it under Portals &amp; team.</p>
        </div>
      </DashboardLayout>
    );
  }
  if (flag && !hasFlag(flag)) {
    return (
      <DashboardLayout>
        <LockedFeature testId={`${flag}-locked`} requiredPlan={flagPlan} title={flagTitle}
          description={flagBody} />
      </DashboardLayout>
    );
  }
  return layout ? <DashboardLayout>{children}</DashboardLayout> : children;
}

const OPERATOR = ["owner", "manager", "dispatcher", "accountant"];

/** A phone keeps its scroll offset across a route change, so signing in from a form below the fold
 *  used to land the next page mid-page. */
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

// A store build is this same app with one route as its home: the operator's own passenger app, or
// the chauffeur app. Everything else (the marketing site, the other doors) is out of its way.
const NATIVE_HOME = process.env.REACT_APP_NATIVE_HOME || "";
if (NATIVE_HOME) document.documentElement.classList.add("native-app");

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <AppManifest />
        <Routes>
          <Route path="/" element={NATIVE_HOME ? <Navigate to={NATIVE_HOME} replace /> : <Landing />} />
          <Route path="/clients" element={<Navigate to="/portal/client/login" replace />} />
          <Route path="/drivers" element={<Navigate to="/portal/driver/login" replace />} />
          <Route path="/office" element={<Navigate to="/app/login" replace />} />
          <Route path="/portal/client/login" element={<Door door="clients" />} />
          <Route path="/portal/driver/login" element={<Door door="drivers" />} />
          <Route path="/app/login" element={<Door door="office" />} />
          <Route path="/admin" element={<AdminGate />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/set-password/:token" element={<SetPassword />} />
          <Route path="/legal/sms-optin" element={<SmsOptIn />} />
          <Route path="/legal/sms-optin/:companyId" element={<SmsOptIn />} />
          <Route path="/legal/:doc" element={<Legal />} />
          <Route path="/testers" element={<Testers />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<Blog />} />
          <Route path="/book/:companyId" element={<PublicBooking />} />
          <Route path="/book/:companyId/blog" element={<OperatorBlog />} />
          <Route path="/book/:companyId/blog/:slug" element={<OperatorBlog />} />
          <Route path="/route/:companyId/:slug" element={<RouteLanding />} />
          {/* The installed rider app owns /ride/<handle>/ — the whole passenger journey lives
              inside that scope so a tap never breaks out into a browser tab. */}
          <Route path="/ride/:companyId" element={<RiderApp />} />
          <Route path="/ride/:companyId/book" element={<PublicBooking />} />
          <Route path="/ride/:companyId/trips/:token" element={<RiderTrips />} />
          <Route path="/ride/:companyId/track/:token" element={<TrackTrip />} />
          <Route path="/ride/:companyId/receipt/:token" element={<TripReceipt />} />
          <Route path="/ride/:companyId/pay/:token" element={<PayTrip />} />
          <Route path="/ride/:companyId/paid" element={<BookingPaid />} />
          {/* Links already sitting in passengers' inboxes: each one redirects into the app's
              scope as soon as we know which operator the token belongs to. */}
          <Route path="/r/:token" element={<RiderTrips />} />
          <Route path="/winback/:token" element={<Winback />} />
          <Route path="/booking/paid" element={<BookingPaid />} />
          <Route path="/driver/trip/:token" element={<DriverTrip />} />
          <Route path="/track/:token" element={<TrackTrip />} />
          <Route path="/receipt/:token" element={<TripReceipt />} />
          <Route path="/pay/:token" element={<PayTrip />} />
          <Route path="/card/:token" element={<SaveCard />} />

          <Route path="/statement/:token" element={<StatementView />} />
          <Route path="/corporate/:token" element={<CorporatePortal />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />

          <Route path="/portal/driver" element={<Guard roles={["driver"]}><DriverPortal /></Guard>} />
          <Route path="/portal/driver/earnings" element={<Guard roles={["driver"]}><DriverEarnings /></Guard>} />
          <Route path="/portal/driver/manifests" element={<Guard roles={["driver"]}><DriverManifests /></Guard>} />
          <Route path="/portal/driver/account" element={<Guard roles={["driver"]}><PortalAccount role="driver" /></Guard>} />
          {/* The community lives inside whichever app you are signed in to, so a chauffeur reading
              their union chat is never bounced out to the office door. */}
          <Route path="/community" element={<CommunityBridge />} />
          <Route path="/community/:id" element={<CommunityBridge />} />
          <Route path="/portal/driver/community" element={<Guard roles={["driver"]}><CommunityFrame><Community /></CommunityFrame></Guard>} />
          <Route path="/portal/driver/community/:id" element={<Guard roles={["driver"]}><CommunityFrame><CommunityRoom /></CommunityFrame></Guard>} />
          <Route path="/admin/community" element={<Guard roles={["platform_admin"]}><CommunityFrame><Community /></CommunityFrame></Guard>} />
          <Route path="/admin/community/:id" element={<Guard roles={["platform_admin"]}><CommunityFrame><CommunityRoom /></CommunityFrame></Guard>} />
          <Route path="/portal/client" element={<Guard roles={["client"]}><ClientPortal /></Guard>} />
          <Route path="/portal/client/account" element={<Guard roles={["client"]}><PortalAccount role="client" /></Guard>} />
          {/* The client portal is its own installable app too — its passenger pages stay inside
              /portal/client so tracking or paying never opens a browser tab. */}
          <Route path="/portal/client/track/:token" element={<TrackTrip />} />
          <Route path="/portal/client/receipt/:token" element={<TripReceipt />} />
          <Route path="/portal/client/pay/:token" element={<PayTrip />} />
          <Route path="/portal/invoice/:id" element={<Guard roles={["client"]} layout={false}><div className="min-h-screen bg-ink-900 p-6"><InvoiceView /></div></Guard>} />

          <Route path="/app" element={<Guard roles={OPERATOR} perm="reports" layout><Dashboard /></Guard>} />
          <Route path="/app/dispatch" element={<Guard roles={OPERATOR} perm="dispatch" flag="dispatch" flagPlan="Starter" flagTitle="The dispatch board is a paid feature" flagBody="See every trip of the day on one board, assign chauffeurs and watch jobs move from offered to complete. Available from the Starter plan." layout><Dispatch /></Guard>} />
          <Route path="/app/dispatch/live" element={<Guard roles={OPERATOR} perm="dispatch" flag="dispatch" flagPlan="Starter" flagTitle="The dispatch board is a paid feature" flagBody="Follow your cars on a live map as they run the day's work. Available from the Starter plan." layout><DispatchLive /></Guard>} />
          <Route path="/app/manifests" element={<Guard roles={OPERATOR} perm="trips" layout><Manifests /></Guard>} />
          <Route path="/app/trips" element={<Guard roles={OPERATOR} perm="trips" layout><Manifests /></Guard>} />
          <Route path="/app/airport-manifests" element={<Guard roles={OPERATOR} perm="trips" layout><AirportManifests /></Guard>} />
          <Route path="/app/manifests/new" element={<Guard roles={OPERATOR} perm="trips" layout><ManifestForm /></Guard>} />
          <Route path="/app/manifests/:id" element={<Guard roles={OPERATOR} perm="trips" layout><ManifestView /></Guard>} />
          <Route path="/app/manifests/:id/edit" element={<Guard roles={OPERATOR} perm="trips" layout><ManifestForm /></Guard>} />
          <Route path="/app/bookings" element={<Guard roles={OPERATOR} perm="trips" layout><Bookings /></Guard>} />
          <Route path="/app/inbox" element={<Guard roles={OPERATOR} perm="dispatch" flag="dispatch" flagPlan="Starter" flagTitle="The request inbox is a paid feature" flagBody="Every pickup change, extra stop and booking request your passengers ask for, on one screen with approve and decline on each row. Available from the Starter plan." layout><Inbox /></Guard>} />
          <Route path="/app/invoices" element={<Guard roles={OPERATOR} perm="payments" flag="invoices" flagPlan="Professional" flagTitle="Invoicing is a paid feature" flagBody="Send branded PDF invoices, chase what's owed automatically and mark trips paid. Available from the Professional plan." layout><Invoices /></Guard>} />
          <Route path="/app/corporate" element={<Guard roles={OPERATOR} perm="payments" flag="corporate" flagPlan="Professional" flagTitle="Corporate accounts are a paid feature" flagBody="Bill companies on Net-30 terms, give their travellers a booking portal and send monthly statements. Available from the Professional plan." layout><Corporate /></Guard>} />
          <Route path="/app/invoices/:id" element={<Guard roles={OPERATOR} perm="payments" flag="invoices" flagPlan="Professional" flagTitle="Invoicing is a paid feature" flagBody="Send branded PDF invoices and chase what's owed automatically. Available from the Professional plan." layout><InvoiceView /></Guard>} />
          <Route path="/app/vehicles" element={<Guard roles={OPERATOR} perm="vehicles" layout><Vehicles /></Guard>} />
          <Route path="/app/chauffeurs" element={<Guard roles={OPERATOR} perm="vehicles" layout><Chauffeurs /></Guard>} />
          <Route path="/app/affiliates" element={<Guard roles={OPERATOR} perm="affiliates" flag="affiliates" flagPlan="Professional" flagTitle="The affiliate network is a paid feature" flagBody="Pass overflow trips to partner operators, take their farm-in work and rate who you'd use again. Available from the Professional plan." layout><Affiliates /></Guard>} />
          <Route path="/app/payouts" element={<Guard roles={OPERATOR} perm="payouts" flag="reporting" flagPlan="Starter" flagTitle="Payouts & 1099s are a paid feature" flagBody="Settle chauffeur payouts, export statements and produce year-end 1099s. Available from the Starter plan." layout><Payouts /></Guard>} />
          <Route path="/app/team" element={<Guard roles={["owner"]} layout><Team /></Guard>} />
          <Route path="/app/community" element={<Guard roles={OPERATOR} layout><Community /></Guard>} />
          <Route path="/app/community/:id" element={<Guard roles={OPERATOR} layout><CommunityRoom /></Guard>} />
          <Route path="/app/settings" element={<Guard roles={OPERATOR} perm="settings" layout><Settings /></Guard>} />
          <Route path="/app/blog" element={<Guard roles={OPERATOR} perm="settings" flag="blog" flagPlan="Professional" flagTitle="Your own blog is a paid feature" flagBody="Publish posts on your booking site that bring in bookings, with the first draft written for you. Available from the Professional plan." layout><BlogDesk /></Guard>} />
          <Route path="/app/profile" element={<Guard roles={OPERATOR} layout><Profile /></Guard>} />
          <Route path="/app/plans" element={<Guard roles={["owner"]} layout><Plans /></Guard>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
